/**
 * Chunk Storage Service
 * Saves encrypted audio chunks incrementally during recording
 * Enables recovery of recordings that were interrupted before completion
 */

import { v4 as uuidv4 } from 'uuid';
import { generateEncryptionKey, encryptAudioBlob, exportKey, importKey, decryptAudioBlob, AUDIO_ENCRYPTION_CONFIG } from '@/utils/crypto';

export interface RecordingChunk {
  id: string;
  sessionId: string;
  segmentIndex: number;
  chunkIndex: number;
  encryptedData: ArrayBuffer;
  encryptionMetadata: {
    iv: string;
    authTag: string;
    algorithm: string;
    keyId: string;
  };
  timestamp: number;
  duration: number; // in ms
  size: number; // bytes
  mimeType: string;
}

export interface ChunkSaveResult {
  chunkId: string;
  chunkIndex: number;
  timestamp: number;
}

// Database configuration
const DB_NAME = 'AmbientListeningChunks';
const DB_VERSION = 1;
const STORE_NAME = 'chunks';
const KEY_STORE_NAME = 'encryptionKeys';

class ChunkStorageService {
  private db: IDBDatabase | null = null;
  private initialized = false;

  // In-memory buffer for accumulating chunks before save
  private chunkBuffer: Blob[] = [];
  private bufferStartTime: number = 0;
  private currentSessionId: string | null = null;
  private currentSegmentIndex: number = 0;
  private currentChunkIndex: number = 0;
  private encryptionKey: CryptoKey | null = null;
  private encryptionKeyId: string | null = null;
  private mimeType: string = 'audio/webm';

  // Configuration
  private readonly SAVE_INTERVAL_MS = 30000; // Save every 30 seconds
  private readonly MIN_CHUNK_SIZE = 1024; // Minimum 1KB to save

  /**
   * Initialize the database
   */
  async initialize(): Promise<void> {
    if (this.initialized && this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[ChunkStorage] Failed to open IndexedDB:', request.error);
        reject(new Error('Chunk database initialization failed'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.initialized = true;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create chunks store
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('sessionId', 'sessionId', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('sessionSegment', ['sessionId', 'segmentIndex'], { unique: false });
        }

        // Create encryption keys store
        if (!db.objectStoreNames.contains(KEY_STORE_NAME)) {
          const keyStore = db.createObjectStore(KEY_STORE_NAME, { keyPath: 'keyId' });
          keyStore.createIndex('sessionId', 'sessionId', { unique: false });
        }
      };
    });
  }

  /**
   * Start a new recording session for chunk storage
   */
  async startSession(sessionId: string, mimeType: string = 'audio/webm'): Promise<string> {
    await this.initialize();

    // Generate encryption key for this session
    this.encryptionKey = await generateEncryptionKey();
    this.encryptionKeyId = await exportKey(this.encryptionKey);

    // Store the key reference
    await this.saveEncryptionKey(sessionId, this.encryptionKeyId);

    this.currentSessionId = sessionId;
    this.currentSegmentIndex = 0;
    this.currentChunkIndex = 0;
    this.chunkBuffer = [];
    this.bufferStartTime = Date.now();
    this.mimeType = mimeType;

    return this.encryptionKeyId;
  }

  /**
   * Save encryption key reference for session
   */
  private async saveEncryptionKey(sessionId: string, keyId: string): Promise<void> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([KEY_STORE_NAME], 'readwrite');
      const store = transaction.objectStore(KEY_STORE_NAME);

      const keyRecord = {
        keyId,
        sessionId,
        createdAt: Date.now(),
      };

      const request = store.put(keyRecord);

      request.onsuccess = () => resolve();

      request.onerror = () => {
        console.error('[ChunkStorage] Failed to save encryption key:', request.error);
        reject(new Error('Failed to save encryption key'));
      };
    });
  }

  /**
   * Add audio data to buffer
   * Call this with each chunk from MediaRecorder ondataavailable
   */
  addToBuffer(blob: Blob): void {
    if (!this.currentSessionId) {
      console.warn('[ChunkStorage] No active session - cannot buffer');
      return;
    }

    this.chunkBuffer.push(blob);
  }

  /**
   * Check if buffer should be flushed based on time
   */
  shouldFlushBuffer(): boolean {
    if (!this.bufferStartTime || this.chunkBuffer.length === 0) {
      return false;
    }
    return Date.now() - this.bufferStartTime >= this.SAVE_INTERVAL_MS;
  }

  /**
   * Get buffer size in bytes
   */
  getBufferSize(): number {
    return this.chunkBuffer.reduce((total, blob) => total + blob.size, 0);
  }

  /**
   * Flush buffer - encrypt and save accumulated chunks
   * @param force Force flush even if buffer is small
   */
  async flushBuffer(force: boolean = false): Promise<ChunkSaveResult | null> {
    if (!this.currentSessionId || !this.encryptionKey) {
      console.warn('[ChunkStorage] No active session or key - cannot flush');
      return null;
    }

    if (this.chunkBuffer.length === 0) {
      return null;
    }

    const bufferSize = this.getBufferSize();
    if (!force && bufferSize < this.MIN_CHUNK_SIZE) {
      return null;
    }

    try {
      // Combine buffered blobs
      const combinedBlob = new Blob(this.chunkBuffer, { type: this.mimeType });
      const duration = Date.now() - this.bufferStartTime;

      // Encrypt the combined blob
      const encrypted = await encryptAudioBlob(combinedBlob, this.encryptionKey);

      // Create chunk record
      const chunk: RecordingChunk = {
        id: uuidv4(),
        sessionId: this.currentSessionId,
        segmentIndex: this.currentSegmentIndex,
        chunkIndex: this.currentChunkIndex,
        encryptedData: encrypted.encryptedData,
        encryptionMetadata: {
          iv: encrypted.iv,
          authTag: encrypted.authTag,
          algorithm: AUDIO_ENCRYPTION_CONFIG.algorithm,
          keyId: this.encryptionKeyId!,
        },
        timestamp: this.bufferStartTime,
        duration,
        size: combinedBlob.size,
        mimeType: this.mimeType,
      };

      // Save to IndexedDB
      await this.saveChunk(chunk);

      // Reset buffer
      const result: ChunkSaveResult = {
        chunkId: chunk.id,
        chunkIndex: this.currentChunkIndex,
        timestamp: chunk.timestamp,
      };

      this.currentChunkIndex++;
      this.chunkBuffer = [];
      this.bufferStartTime = Date.now();

      return result;
    } catch (error) {
      console.error('[ChunkStorage] Failed to flush buffer:', error);
      throw error;
    }
  }

  /**
   * Save a chunk to IndexedDB
   */
  private async saveChunk(chunk: RecordingChunk): Promise<void> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(chunk);

      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.error('[ChunkStorage] Failed to save chunk:', request.error);
        reject(new Error('Failed to save chunk'));
      };
    });
  }

  /**
   * Start a new segment (called when recording resumes after pause)
   */
  startNewSegment(): void {
    if (!this.currentSessionId) {
      console.warn('[ChunkStorage] No active session - cannot start segment');
      return;
    }

    this.currentSegmentIndex++;
    this.currentChunkIndex = 0;
    this.chunkBuffer = [];
    this.bufferStartTime = Date.now();
  }

  /**
   * End current session
   * Flushes any remaining buffer
   */
  async endSession(): Promise<ChunkSaveResult | null> {
    if (!this.currentSessionId) {
      return null;
    }

    // Flush any remaining buffer
    const result = await this.flushBuffer(true);

    // Reset state
    this.currentSessionId = null;
    this.encryptionKey = null;
    this.encryptionKeyId = null;
    this.currentSegmentIndex = 0;
    this.currentChunkIndex = 0;
    this.chunkBuffer = [];
    this.bufferStartTime = 0;

    return result;
  }

  /**
   * Get all chunks for a session
   */
  async getSessionChunks(sessionId: string): Promise<RecordingChunk[]> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('sessionId');
      const request = index.getAll(sessionId);

      request.onsuccess = () => {
        const chunks = request.result as RecordingChunk[];
        // Sort by segment and chunk index
        chunks.sort((a, b) => {
          if (a.segmentIndex !== b.segmentIndex) {
            return a.segmentIndex - b.segmentIndex;
          }
          return a.chunkIndex - b.chunkIndex;
        });
        resolve(chunks);
      };

      request.onerror = () => {
        console.error('[ChunkStorage] Failed to get session chunks:', request.error);
        reject(new Error('Failed to retrieve chunks'));
      };
    });
  }

  /**
   * Get chunk count for a session
   */
  async getSessionChunkCount(sessionId: string): Promise<number> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('sessionId');
      const request = index.count(sessionId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        console.error('[ChunkStorage] Failed to count chunks:', request.error);
        reject(new Error('Failed to count chunks'));
      };
    });
  }

  /**
   * Get encryption key ID for a session
   */
  async getSessionKeyId(sessionId: string): Promise<string | null> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([KEY_STORE_NAME], 'readonly');
      const store = transaction.objectStore(KEY_STORE_NAME);
      const index = store.index('sessionId');
      const request = index.get(sessionId);

      request.onsuccess = () => {
        resolve(request.result?.keyId || null);
      };

      request.onerror = () => {
        console.error('[ChunkStorage] Failed to get key ID:', request.error);
        reject(new Error('Failed to retrieve key'));
      };
    });
  }

  /**
   * Delete all chunks for a session
   */
  async deleteSessionChunks(sessionId: string): Promise<void> {
    if (!this.db) await this.initialize();

    const chunks = await this.getSessionChunks(sessionId);

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME, KEY_STORE_NAME], 'readwrite');
      const chunkStore = transaction.objectStore(STORE_NAME);
      const keyStore = transaction.objectStore(KEY_STORE_NAME);

      let deleteCount = 0;

      // Delete all chunks
      for (const chunk of chunks) {
        const request = chunkStore.delete(chunk.id);
        request.onsuccess = () => deleteCount++;
      }

      // Delete encryption key
      const keyIndex = keyStore.index('sessionId');
      const keyRequest = keyIndex.openCursor(sessionId);
      keyRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      transaction.oncomplete = () => {
        resolve();
      };

      transaction.onerror = () => {
        console.error('[ChunkStorage] Failed to delete chunks:', transaction.error);
        reject(new Error('Failed to delete session chunks'));
      };
    });
  }

  // TODO: Add orphaned chunk detection and cleanup.
  // Orphaned chunks are those with no matching active session in the recording session store.
  // This requires cross-store access (chunks DB + session store) which is not straightforward
  // from IndexedDB alone. Consider implementing when a unified storage coordinator is built.

  /**
   * Get total storage used by chunks
   */
  async getStorageStats(): Promise<{
    totalChunks: number;
    totalSize: number;
    sessionCount: number;
  }> {
    if (!this.db) await this.initialize();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const chunks = request.result as RecordingChunk[];
        const sessions = new Set(chunks.map(c => c.sessionId));

        resolve({
          totalChunks: chunks.length,
          totalSize: chunks.reduce((sum, c) => sum + c.size, 0),
          sessionCount: sessions.size,
        });
      };

      request.onerror = () => {
        console.error('[ChunkStorage] Failed to get stats:', request.error);
        reject(new Error('Failed to get storage stats'));
      };
    });
  }

  /**
   * Get current encryption key ID (for session store)
   */
  getCurrentKeyId(): string | null {
    return this.encryptionKeyId;
  }

  /**
   * Get current session ID
   */
  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Check if there's an active session
   */
  hasActiveSession(): boolean {
    return this.currentSessionId !== null;
  }
}

// Export singleton instance
export const chunkStorageService = new ChunkStorageService();
